import { Avatar, Box, Card, Chip, Divider, Grid, Stack, Typography } from '@mui/material';

import { CheckCircle, ChevronRight, Plus } from '@opengov/react-capital-assets';

import { MetricCard } from '../components/shared/MetricCard';
import { DenseRow, IconBadge } from '../components/vcm/DenseRow';
import { SectionCard } from '../components/vcm/SectionCard';
import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetResources } from '../mocks/vcm-mock-data';

/** Count occurrences of a key across items, returned as descending rows. */
function countRows(values: string[]): { label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

export function ResourceDashboard() {
  const { navigateToPath } = useAppNavigation();
  const { moduleLabel, role, currentEvent, currentUser } = useVcm();

  /* MOCK DATA — all metrics derived from the resource library */
  const resources = mockGetResources();
  const total = resources.length;
  const active = resources.filter((r) => r.status === 'Active').length;
  const needReview = total - active;
  const referralReadyPct = total === 0 ? 0 : Math.round((active / total) * 100);

  const countyRows = countRows(resources.map((r) => r.county));
  const categoryRows = countRows(resources.map((r) => r.category)).slice(0, 5);

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      {/* ── Welcome Banner ── */}
      <Card>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{
            gap: (theme) => theme.spacing(2),
            px: (theme) => theme.spacing(2.5),
            py: (theme) => theme.spacing(2),
            background: (theme) =>
              `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.background.paper})`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: (theme) => theme.palette.primary.main,
                fontSize: (theme) => theme.typography.body2.fontSize,
                fontWeight: 700,
              }}
            >
              {currentUser.initials}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Resource Management
              </Typography>
              <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>
                {role} &middot; {moduleLabel} &middot; {currentEvent.name} &middot;{' '}
                {currentEvent.programCode}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            spacing={3}
            sx={{ alignItems: 'center' }}
          >
            <BannerStat value={active} label="Active" />
            <BannerStat value={needReview} label="Need review" tone="warning" />
            <BannerStat value={total} label="Total" />
          </Stack>
        </Stack>
      </Card>

      {/* ── 4 Metric Cards ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Active resources"
            value={String(active)}
            subtitle="Available to survivors"
            onClick={() => navigateToPath('resources/library')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Need review"
            value={String(needReview)}
            subtitle="Awaiting verification"
            onClick={() => navigateToPath('resources/manage')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Referral-ready"
            value={`${referralReadyPct}%`}
            subtitle="Active share of library"
            onClick={() => navigateToPath('resources/library')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            center
            title="Total resources"
            value={String(total)}
            subtitle="In the resource library"
            onClick={() => navigateToPath('resources/manage')}
          />
        </Grid>
      </Grid>

      {/* ── Coverage by county + Top service categories ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Coverage by County">
            <Stack spacing={1}>
              {countyRows.map((row) => (
                <CountRow key={row.label} label={row.label} count={row.count} />
              ))}
              {countyRows.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  No resources on record.
                </Typography>
              )}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title="Top Service Categories">
            <Stack spacing={1}>
              {categoryRows.map((row) => (
                <CountRow key={row.label} label={row.label} count={row.count} />
              ))}
              {categoryRows.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  No service categories on record.
                </Typography>
              )}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── Needs Attention ── */}
      <SectionCard title="Needs Attention">
        <Stack>
          <DenseRow
            leading={
              <IconBadge tone={needReview > 0 ? 'warning' : 'success'}>
                <CheckCircle />
              </IconBadge>
            }
            primary={
              needReview > 0
                ? `${needReview} resource${needReview !== 1 ? 's' : ''} need review`
                : 'All resources are active'
            }
            secondary="Open the resource manager to verify status"
            trailing={
              <Box
                sx={{
                  display: 'flex',
                  color: (theme) => theme.palette.text.secondary,
                  '& svg': { width: 16, height: 16 },
                }}
              >
                <ChevronRight />
              </Box>
            }
            onClick={() => navigateToPath('resources/manage')}
          />
          <DenseRow
            leading={
              <IconBadge tone="primary">
                <Plus />
              </IconBadge>
            }
            primary="Add resources for underserved counties"
            secondary="Expand coverage in the resource library"
            trailing={
              <Box
                sx={{
                  display: 'flex',
                  color: (theme) => theme.palette.text.secondary,
                  '& svg': { width: 16, height: 16 },
                }}
              >
                <ChevronRight />
              </Box>
            }
            onClick={() => navigateToPath('resources/library')}
          />
        </Stack>
      </SectionCard>
    </Box>
  );
}

function CountRow({ label, count }: { readonly label: string; readonly count: number }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Typography variant="body2">{label}</Typography>
      <Chip label={count} size="small" variant="outlined" />
    </Stack>
  );
}

function BannerStat({
  value,
  label,
  tone = 'primary',
}: {
  readonly value: number;
  readonly label: string;
  readonly tone?: 'primary' | 'warning';
}) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: (theme) =>
            tone === 'primary' ? theme.palette.text.primary : theme.palette[tone].main,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
      >
        {label}
      </Typography>
    </Box>
  );
}
